import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileText, Loader2, ScrollText, X } from 'lucide-react';

/**
 * Ouvre le report.html / log.html Robot Framework d'un build, extrait de l'artefact
 * Azure DevOps par la route backend GET /api/rf-report/:buildId/:kind.
 *
 * Deux contraintes dictent l'implémentation :
 *
 * 1. Un <iframe src> ne peut pas porter le header X-Azure-Devops-Pat. On fait donc
 *    un fetch() authentifié, puis on passe par un blob: URL.
 * 2. Le log.html de Robot Framework embarque du JS inline et le contenu des tests.
 *    Servi sur l'origine du dashboard, il pourrait lire localStorage.AZDO_PAT.
 *    L'iframe est donc sandboxée SANS allow-same-origin : origine opaque, aucun
 *    accès au stockage du dashboard. Le backend pose en plus l'en-tête
 *    `Content-Security-Policy: sandbox allow-scripts` pour couvrir le cas d'une
 *    ouverture directe de l'URL dans un onglet.
 *
 * Le log.html d'une grosse suite pèse plusieurs dizaines de Mo : `report` est le
 * défaut (synthèse, léger), `log` reste un second clic explicite.
 */

const PAT_STORAGE_KEY = 'AZDO_PAT';

const KINDS = {
  report: { label: 'Rapport', icon: FileText, title: 'Rapport Robot Framework' },
  log: { label: 'Log', icon: ScrollText, title: 'Log Robot Framework' }
};

export default function RobotReportButton({ buildId, artifactName, kind = 'report', className = '' }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const blobUrlRef = useRef([]);

  const config = KINDS[kind] || KINDS.report;
  const Icon = config.icon;

  const revoke = useCallback(() => {
    blobUrlRef.current.forEach(URL.revokeObjectURL);
    blobUrlRef.current = [];
  }, []);

  useEffect(() => revoke, [revoke]);

  const close = useCallback(() => {
    revoke();
    setBlobUrl(null);
    setError(null);
  }, [revoke]);

  useEffect(() => {
    if (!blobUrl) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [blobUrl, close]);

  const fetchPart = async (which) => {
    const pat = localStorage.getItem(PAT_STORAGE_KEY) || '';
    const query = artifactName ? `?artifact=${encodeURIComponent(artifactName)}` : '';
    const res = await fetch(`/api/rf-report/${encodeURIComponent(buildId)}/${which}${query}`, {
      headers: { 'X-Azure-Devops-Pat': pat }
    });
    if (!res.ok) {
      // La route répond en JSON sur erreur, en HTML sur succès. Si le backend est
      // injoignable, le proxy CRA renvoie du HTML : le parse échoue et on le dit.
      const payload = await res.json().catch(() => null);
      if (payload?.error) throw new Error(payload.error);
      throw new Error(
        res.status >= 500
          ? `Backend injoignable ou en erreur (HTTP ${res.status}). Le serveur du port 3001 tourne-t-il ?`
          : `HTTP ${res.status}`
      );
    }
    return res.text();
  };

  const toBlobUrl = (html) => {
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    blobUrlRef.current.push(url);
    return url;
  };

  const open = async () => {
    if (!buildId) { setError('Aucun build associé.'); return; }
    setLoading(true);
    setError(null);
    try {
      let html = await fetchPart(kind);
      revoke();

      // Le report.html de Robot Framework pointe vers le log via
      // `window.settings.logURL = "log.html"`. Depuis une URL blob:, ce chemin
      // relatif ne se résout sur rien — d'où une page blanche au clic. On le
      // remplace par le blob: du log, ce qui préserve aussi les ancres #s1-s2-t3.
      // Le backend a extrait les deux fichiers du même ZIP, donc cet appel est
      // servi depuis son cache.
      if (kind === 'report') {
        try {
          const logUrl = toBlobUrl(await fetchPart('log'));
          html = html.replace(/"logURL"\s*:\s*"[^"]*"/, `"logURL":${JSON.stringify(logUrl)}`);
        } catch (e) {
          // Log indisponible : le rapport reste lisible, seuls ses liens sont morts.
          console.warn('[RobotReportButton] log.html indisponible, liens du rapport inactifs:', e.message);
        }
      }

      setBlobUrl(toBlobUrl(html));
    } catch (err) {
      setError(err.message || 'Récupération impossible');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={open}
        disabled={loading}
        title={`${config.title} — build ${buildId}`}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-blue-600 disabled:opacity-50 transition-colors ${className}`}
      >
        {loading
          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
          : <Icon className="w-3.5 h-3.5" />}
        {config.label}
      </button>

      {error && (
        <span className="ml-2 text-xs text-red-600" role="alert">{error}</span>
      )}

      {blobUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={close}
          role="presentation"
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full h-full max-w-7xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 shrink-0">
              <span className="text-sm font-semibold text-gray-900">
                {config.title} — build {buildId}
              </span>
              <button
                type="button"
                onClick={close}
                aria-label="Fermer"
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {/* sandbox sans allow-same-origin : origine opaque, pas d'accès au PAT */}
            <iframe
              src={blobUrl}
              title={config.title}
              sandbox="allow-scripts"
              className="flex-1 w-full border-0"
            />
          </div>
        </div>
      )}
    </>
  );
}
